import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Form from '@rjsf/mui';
import { RJSFSchema } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';
import { useSnackbar } from 'notistack';

import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { Box, Button, CircularProgress, MenuItem, Stack, TextField } from '@mui/material';

import useCurrentUser from '~/hooks/useCurrentUser';

import { RoomPolicy } from '~/types/roomPolicy';

import OffersService from '../../../services/OffersService';
import parseRoomPolicyDescription from '../../../utils/parseRoomPolicyDescription';
import { reportError } from '../../../utils/reportError';
import ErrorListDisplay from '../ErrorListDisplay';

import RoomPolicyFormTextWidget from './RoomPolicyFormWidget';
import RoomPolicyTableEditor from './RoomPolicyTableEditor';
import { buttonMessages, buttonStates } from './states/submitButton';
import MarkdownEditor from './widgets/MarkdownEditor';

export type RoomPolicyLanguage = 'en' | 'de' | 'fr';

interface RoomPolicyFormProps {
  schema: RJSFSchema;
  roomPolicy: RoomPolicy;
  idOffer: string;
  offerType: string;
  hideSaveButton?: boolean;
  onRoomPolicyChange?: (roomPolicy: RoomPolicy) => void;
}

export default function RoomPolicyForm({
  schema: propsSchema,
  roomPolicy: initialRoomPolicy,
  idOffer,
  offerType,
  hideSaveButton = false,
  onRoomPolicyChange,
}: RoomPolicyFormProps) {
  const { enqueueSnackbar } = useSnackbar();
  const canSyncRoomPolicy = offerType === 'hotel';
  const { user } = useCurrentUser();
  const isLanguageFeatureEnabledForUser =
    user?.email?.toLowerCase() === 'gearoid.sheehan@luxuryescapes.com' ||
    user?.email?.toLowerCase() === 'min.pangastur@luxuryescapes.com';

  const [apiErrors, setApiErrors] = useState([]);
  const [roomPolicy, setRoomPolicy] = useState(initialRoomPolicy);
  const [saveState, setSaveState] = useState(buttonStates.default);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoadingTranslation, setIsLoadingTranslation] = useState(false);
  const [language, setLanguage] = useState<RoomPolicyLanguage>('en');
  const [formRenderVersion, setFormRenderVersion] = useState(0);
  const shouldNotifyRoomPolicyChangeRef = useRef(false);
  const [textDescriptionBackup, setTextDescriptionBackup] = useState<string>(() => {
    const initialDescription = initialRoomPolicy?.description ?? '';
    const initialParsed = parseRoomPolicyDescription(initialDescription);
    return initialParsed.isStructured ? '' : initialDescription;
  });

  const parsedDescription = useMemo(
    () => parseRoomPolicyDescription(roomPolicy?.description),
    [roomPolicy?.description],
  );

  const canShowLanguageSelect = canSyncRoomPolicy && isLanguageFeatureEnabledForUser;
  const allowLanguageSelection = canShowLanguageSelect && !!roomPolicy?.id;

  const [structuredPolicyData, setStructuredPolicyData] = useState(() =>
    parsedDescription.isStructured ? parsedDescription.data : null,
  );

  useEffect(() => {
    setRoomPolicy(initialRoomPolicy);
    shouldNotifyRoomPolicyChangeRef.current = false;
    const nextDescription = initialRoomPolicy?.description ?? '';
    const nextParsed = parseRoomPolicyDescription(nextDescription);
    setTextDescriptionBackup(nextParsed.isStructured ? '' : nextDescription);
  }, [initialRoomPolicy]);

  useEffect(() => {
    if (!shouldNotifyRoomPolicyChangeRef.current) {
      return;
    }

    shouldNotifyRoomPolicyChangeRef.current = false;
    onRoomPolicyChange?.(roomPolicy);
  }, [onRoomPolicyChange, roomPolicy]);

  useEffect(() => {
    if (parsedDescription.isStructured) {
      setStructuredPolicyData(parsedDescription.data);
    } else {
      setStructuredPolicyData(null);
    }
  }, [parsedDescription]);

  const updateRoomPolicy = useCallback(
    (nextRoomPolicyOrUpdater: RoomPolicy | ((currentRoomPolicy: RoomPolicy) => RoomPolicy)) => {
      shouldNotifyRoomPolicyChangeRef.current = true;
      setRoomPolicy((prev) => {
        return typeof nextRoomPolicyOrUpdater === 'function' ? nextRoomPolicyOrUpdater(prev) : nextRoomPolicyOrUpdater;
      });
    },
    [],
  );

  const onChange = useCallback(
    (edit) => {
      updateRoomPolicy(edit.formData);
      if (saveState !== buttonStates.default) {
        setSaveState(buttonStates.default);
      }
    },
    [saveState, updateRoomPolicy],
  );

  const onSubmit = useCallback(
    async (form) => {
      form.errors = [];
      form.errorSchema = {};

      setSaveState(buttonStates.saving);

      const submitData = JSON.parse(JSON.stringify(form.formData));
      const idRoomPolicy = submitData.id;
      delete submitData['room_type_name'];
      delete submitData['id'];
      delete submitData['salesforce_id'];
      delete submitData['property_id'];
      delete submitData['room_rate_id'];
      delete submitData['room_rate_ids'];

      try {
        if (idRoomPolicy) {
          delete submitData['room_type_id'];
          await OffersService.updateRoomPolicy(submitData, idOffer, idRoomPolicy, language);
        } else {
          const response = await OffersService.createRoomPolicy(submitData, idOffer, 'en');
          const createdIdCandidate =
            response?.result?.id ?? response?.result?.room_policy_id ?? response?.result?.roomPolicyId;
          const createdId = typeof createdIdCandidate === 'number' ? createdIdCandidate : null;
          if (createdId) {
            updateRoomPolicy((prev) => ({ ...prev, id: createdId }));
          }
        }
        setSaveState(buttonStates.saved);
        setApiErrors([]);
        enqueueSnackbar('Room policy saved successfully', { variant: 'success' });
      } catch (e) {
        const newApiErrors = [];
        if (e.name === 'ValidationError') {
          e.errors.forEach((err) => {
            newApiErrors.push(err.message);
          });
        }
        setSaveState(buttonStates.failed);
        setApiErrors(newApiErrors);
        reportError(e);
        enqueueSnackbar('Failed to save room policy', { variant: 'error' });
      }
    },
    [enqueueSnackbar, idOffer, language, updateRoomPolicy],
  );

  const generateRoomPolicyForLanguage = useCallback(
    async (nextLanguage: RoomPolicyLanguage) => {
      const effectiveLanguage: RoomPolicyLanguage = roomPolicy?.id ? nextLanguage : 'en';
      setIsGenerating(true);
      try {
        const response = await OffersService.generateRoomPolicy(
          initialRoomPolicy.salesforce_id,
          initialRoomPolicy.property_id,
          initialRoomPolicy.room_type_id,
          initialRoomPolicy.room_rate_id,
          initialRoomPolicy.room_rate_ids,
          effectiveLanguage,
        );

        const generatedData = response.result.content;
        const currentDescription = roomPolicy?.description ?? '';

        if (!parseRoomPolicyDescription(currentDescription).isStructured) {
          setTextDescriptionBackup(currentDescription);
        }

        updateRoomPolicy((prev) => ({
          ...prev,
          description: JSON.stringify(generatedData),
        }));
        setSaveState(buttonStates.default);
        enqueueSnackbar('Room policy synced successfully', { variant: 'success' });
      } catch (e) {
        reportError(e);
        enqueueSnackbar('Failed to sync room policy', { variant: 'error' });
      } finally {
        setIsGenerating(false);
      }
    },
    [enqueueSnackbar, initialRoomPolicy, roomPolicy?.description, roomPolicy?.id, updateRoomPolicy],
  );

  const onGenerateRoomPolicy = useCallback(async () => {
    await generateRoomPolicyForLanguage(language);
  }, [generateRoomPolicyForLanguage, language]);

  const fetchTranslatedRoomPolicyForLanguage = useCallback(
    async (nextLanguage: RoomPolicyLanguage) => {
      // If we don't have a room policy id yet, translation route can't be used.
      if (!roomPolicy?.id) {
        await generateRoomPolicyForLanguage(nextLanguage);
        return;
      }

      const previousLanguage = language;
      setLanguage(nextLanguage);
      setIsLoadingTranslation(true);
      try {
        const response = await OffersService.getRoomPolicy(idOffer, roomPolicy.id, nextLanguage);

        const nextDescription =
          response?.result?.description ??
          response?.result?.room_policy_description ??
          response?.description ??
          roomPolicy.description ??
          '';

        const currentDescription = roomPolicy?.description ?? '';
        if (!parseRoomPolicyDescription(currentDescription).isStructured) {
          setTextDescriptionBackup(currentDescription);
        }

        updateRoomPolicy((prev) => ({
          ...prev,
          description: nextDescription,
        }));
        setFormRenderVersion((prev) => prev + 1);

        setSaveState(buttonStates.default);
      } catch (e) {
        setLanguage(previousLanguage);
        reportError(e);
        enqueueSnackbar('Failed to load translated room policy', { variant: 'error' });
      } finally {
        setIsLoadingTranslation(false);
      }
    },
    [
      enqueueSnackbar,
      generateRoomPolicyForLanguage,
      idOffer,
      language,
      roomPolicy?.id,
      roomPolicy?.description,
      updateRoomPolicy,
    ],
  );

  const handleLanguageChange = useCallback(
    (nextLanguage: RoomPolicyLanguage) => {
      if (!allowLanguageSelection) {
        return;
      }
      if (nextLanguage === language) {
        return;
      }
      fetchTranslatedRoomPolicyForLanguage(nextLanguage);
    },
    [allowLanguageSelection, fetchTranslatedRoomPolicyForLanguage, language],
  );

  const onRevertToTextInput = useCallback(() => {
    setStructuredPolicyData(null);
    updateRoomPolicy((prev) => ({
      ...prev,
      description: textDescriptionBackup ?? '',
    }));
    if (saveState !== buttonStates.default) {
      setSaveState(buttonStates.default);
    }
  }, [saveState, textDescriptionBackup, updateRoomPolicy]);

  const handleStructuredPolicyChange = useCallback(
    (nextData) => {
      setStructuredPolicyData(nextData);
      updateRoomPolicy((prev) => ({ ...prev, description: JSON.stringify(nextData) }));
    },
    [updateRoomPolicy],
  );

  const validate = useCallback((formData, errors) => {
    return errors;
  }, []);

  const preventSubmit = useCallback((_, event) => {
    event?.preventDefault();
  }, []);

  const formData = useMemo(() => {
    if (!roomPolicy) {
      return {};
    }
    const data = { ...roomPolicy };
    delete data.property_id;
    delete data.room_rate_id;
    delete data.room_rate_ids;
    return data;
  }, [roomPolicy]);

  if (parsedDescription.isStructured) {
    const dataToShow = structuredPolicyData ?? parsedDescription.data;
    if (dataToShow) {
      return (
        <RoomPolicyTableEditor
          policyData={dataToShow}
          onPolicyDataChange={handleStructuredPolicyChange}
          roomPolicy={roomPolicy}
          idOffer={idOffer}
          onGenerateDefault={onGenerateRoomPolicy}
          isGenerating={isGenerating}
          isLoadingTranslation={isLoadingTranslation}
          onRevertToTextInput={onRevertToTextInput}
          language={language}
          onLanguageChange={allowLanguageSelection ? handleLanguageChange : undefined}
          onRoomPolicyIdChange={(roomPolicyId) => updateRoomPolicy((prev) => ({ ...prev, id: roomPolicyId }))}
          hideSaveButton={hideSaveButton}
        />
      );
    }
  }

  const schema: RJSFSchema = {
    ...propsSchema,
    properties: {
      id: {
        title: 'Room Policy ID',
        type: ['integer', 'null'],
      },
      salesforce_id: {
        title: 'Salesforce ID',
        type: ['string', 'null'],
      },
      ...propsSchema.properties,
      room_type_name: {
        title: 'Room Type Name',
        type: ['string', 'null'],
      },
    },
  };

  const uiSchema = {
    room_type_id: { 'ui:widget': 'hidden' },
    id: { 'ui:widget': 'hidden' },
    salesforce_id: { 'ui:widget': 'hidden' },
    room_type_name: { 'ui:widget': 'hidden' },
    description: { 'ui:widget': MarkdownEditor },
  };

  let apiErrorsDisplay = null;

  if (apiErrors?.length) {
    apiErrorsDisplay = <ErrorListDisplay messages={apiErrors} />;
  }

  return (
    <Box>
      <Form
        key={formRenderVersion}
        schema={schema}
        formData={formData}
        validator={validator}
        showErrorList={false}
        liveValidate
        customValidate={validate}
        onSubmit={hideSaveButton ? preventSubmit : onSubmit}
        onChange={onChange}
        uiSchema={uiSchema}
        widgets={{
          TextWidget: RoomPolicyFormTextWidget,
        }}
        formContext={{
          hideLabel: true,
        }}
      >
        {apiErrorsDisplay}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
          {!hideSaveButton && (
            <Button type="submit" variant="contained" className={saveState}>
              {buttonMessages[saveState]}
            </Button>
          )}
          {canSyncRoomPolicy && (
            <Button
              type="button"
              variant="contained"
              startIcon={<AutoFixHighIcon />}
              onClick={onGenerateRoomPolicy}
              disabled={isGenerating}
            >
              {isGenerating ? 'Syncing...' : 'Sync Room Policy'}
            </Button>
          )}
          <Box sx={{ flexGrow: 1 }} />
          {canShowLanguageSelect && (
            <Box sx={{ minWidth: 200, position: 'relative' }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Language"
                value={allowLanguageSelection ? language : 'en'}
                onChange={(e) => handleLanguageChange(e.target.value as RoomPolicyLanguage)}
                disabled={isLoadingTranslation || !allowLanguageSelection}
              >
                <MenuItem value="en">English</MenuItem>
                <MenuItem value="de">German</MenuItem>
                <MenuItem value="fr">French</MenuItem>
              </TextField>
              {isLoadingTranslation && (
                <CircularProgress
                  size={20}
                  sx={{
                    position: 'absolute',
                    right: 32,
                    top: '50%',
                    mt: '-10px',
                    pointerEvents: 'none',
                  }}
                />
              )}
            </Box>
          )}
        </Stack>
      </Form>
    </Box>
  );
}
